/* eslint-disable react/jsx-max-depth */
import classnames from 'classnames';
import { capitalize, isEmpty, isNil, upperCase } from 'lodash';
import T from 'prop-types';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Dropdown, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { PulseLoader } from 'react-spinners';
import remarkGfm from 'remark-gfm';
import Icon from '../../components/icon/Icon';
import { CONFIRM_MODAL } from '../../components/modal/ConfirmModal';
import useGeocoder from '../../components/RightMenu/useGeocoder';
import Activity from '../../components/timeline/Activity';
import { ACTIVITY_TYPES } from '../../constants/activity';
import { deleteActivity, deleteAsset, showModal } from '../../redux/actions';
import text from '../../text';
import {
  isActivityFileDownload,
  isActivityType,
} from '../../utilities/activity';
import { formatDate, formatTime } from '../../utilities/date';
import { getShortenedAddressFromGeocoder } from '../../utilities/map';
import { isAdministrator, isStaff } from '../../utilities/user';
import ActivityAsset from './ActivityAsset';
import Invoice from './ActivityTemplates/Invoice';
import RequestForQuote from './ActivityTemplates/RFQ';
import JobStatusUpdate from './JobStatusUpdate';
import {
  FloatingPortal,
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  arrow,
} from '@floating-ui/react';
import { useClickOutside } from '../../hooks/useClickOutside';
import { IconButton } from '../../components/button';
import useEventListener from '../../hooks/useEventListener';
import { useSupersedeActivityMutation } from '../../redux/rtk';
import { SUPERSEDE_ACTIVITY } from '../../redux/constants/activity';
import { alertConstants } from '../../redux/constants';
import QuotePdfPreview from '../../components/FileManager/QuotePdfPreview';
import { useFileManagerContext } from '../../hooks/useFileManagerContext';
import { QuotePdfData, QuotePdfProps } from '../../components/FileManager/PdfGenerator';
import { AxiosError } from 'axios';
import { captureError } from '../../utilities/error';
import { DialogTrigger } from '../../mp/components/Dialog';
import { current } from '@reduxjs/toolkit';
import { siteDeploymentFee, total } from '../../text/locale_en';
import { softwares } from '../../components/mapView/constants';

const TypeIconMap = {
  file: 'clip',
  order: 'shopping-cart',
  note: 'speech',
  user: 'user-outline',
  alert: 'larki-icon',
  status: '?',
};

const ActivityTemplate = ({ metadata, job, rfqCharges, quoteCharges }: any) => {
  if (metadata && metadata.action === 'quote') {
    if (rfqCharges) {
      return (
        <RequestForQuote
          title={upperCase(text('requestForQuote'))}
          quote={metadata.quote_data}
          job={job}
          rfqCharges={rfqCharges}
        />
      );
    } else {
      return <Invoice quote={metadata.quote_data} quoteCharges={quoteCharges} job={job} />;
    }
  } else {
    return (
      <>
        <p className='font-weight--600 mb-3 font-18'>
          {metadata && metadata.action === 'invoice'
            ? `${metadata?.type ? upperCase(metadata?.type) : ''
            } INVOICE CREATED`
            : upperCase(text('paymentReceived'))}
        </p>
        <Link
          className='font-weight-bold'
          style={{ textDecoration: 'underline' }}
          key='viewPaymentReceipt'
          to={{ pathname: metadata.receipt_url }}
          target='_blank'
        >
          {metadata && metadata.action === 'invoice'
            ? 'View Invoice'
            : text('viewPaymentReceipt')}
        </Link>
      </>
    );
  }
};

ActivityTemplate.propTypes = {
  metadata: T.shape({
    action: T.string,
    receipt_url: T.string,
    type: T.string,
  }),
  rfqCharges: T.array,
  job: T.object,
};

// TODO: Refactor this to project activity
const JobActivity = (props: any) => {
  const {
    jobActivity: {
      id,
      created_at,
      created_assets,
      title,
      description,
      type,
      job_id,
      project_id,
      detailed_status_update,
      is_notification_sent,
      visibility,
      created_user,
      rfqCharges,
      quoteCharges,
      metadata,
      superseded_assets,
    },
    currentProject,
    currentUser,
  } = props;

  const { scrollContainerRef } = useFileManagerContext();

  const [supersedeActivity] = useSupersedeActivityMutation();
  const dispatch = useDispatch();
  const { geocode } = useGeocoder();
  const [shortenedAddress, setShortenedAddress] = useState<string | null>(null);
  const [addressStatus, setAddressStatus] = useState('checking');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const arrowRef = useRef(null);
  const { refs, floatingStyles, middlewareData, placement } = useFloating({
    open: isDropdownOpen,
    onOpenChange: setIsDropdownOpen,
    middleware: [
      offset(6),
      flip(),
      shift(),
      arrow({ element: arrowRef, padding: 5 }),
    ],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-end',
  });

  // Check if the dropdown is flipped to the top
  const isFlipped = placement?.includes('top');

  const arrowStyles = middlewareData.arrow
    ? {
      left:
        middlewareData.arrow.x != null ? `${middlewareData.arrow.x}px` : '',
      top:
        middlewareData.arrow.y != null ? `${middlewareData.arrow.y}px` : '',
      position: 'absolute',
      width: '12px',
      height: '12px',
      background: 'white',
      border: '1px solid rgba(0,0,0,0.15)',
      // Position the arrow based on whether the dropdown is flipped
      ...(isFlipped
        ? {
          bottom: '-6px',
          transform: 'rotate(45deg)',
          borderWidth: '0 1px 1px 0',
        }
        : {
          top: '-6px',
          transform: 'rotate(45deg)',
          borderWidth: '1px 0 0 1px',
        }),
    }
    : {};

  const job = useMemo(
    () => currentProject.jobs.find(({ id }: any) => id === job_id),
    [currentProject.jobs, job_id]
  );

  const quotePdfProps = useMemo(() => {
    try {
      if (!metadata?.quote_data || !job || !currentProject) return null;
      const type = rfqCharges ? 'estimate' : 'quote';
      const charges = rfqCharges ?? quoteCharges;
      if (!charges) return null;
      const siteDeploymentFee = type === 'estimate' ? metadata.quote_data.fees.find((fee: any) => fee.id === 'site_deployment_fee') : null;
      // const totalNoTax = metadata.quote_data.cart_totals.price.subtotal;
      // const tax = metadata.quote_data.cart_totals.taxes.reduce((acc: number, tax: any) => acc + tax.price.extra, 0);
      // const total = totalNoTax + tax;
      let totalNoTax = charges.reduce((acc: number, charge: any) => {
        const discount = charge.discounts?.reduce((acc: number, discount: any) => {
          return acc + discount.price.discount;
        }, 0);
        return acc + charge.price.subtotal - discount
      }, 0);
      if (siteDeploymentFee) {
        totalNoTax += siteDeploymentFee.price.base;
      }
      const tax = totalNoTax * (metadata.quote_data.gst / 100);
      const total = totalNoTax + tax;
      const result: QuotePdfProps = {
        data: {
          type,
          metadata: {
            lastUpdateBy: job.updater?.full_name,
            company: currentProject.organisation?.name,
            date: new Date(job.updated_at),
            email: currentProject.creator?.email,
            jobId: job.id,
            projectId: currentProject?.id,
          },
          siteAddress: job.full_address,
          ...(type === 'quote' ? {
            projectName: currentProject.name ?? 'Untitled',
            // @ts-expect-error
            software: softwares.entities[job.customer_software]?.name,
          } : {}),
          products: charges.map((charge: any) => {
            const discounts = charge.discounts.reduce((acc: number, discount: any) => {
              console.log(discount)
              return acc + discount.price.discount;
            }, 0);
            return {
              dataType: charge.details.category_name,
              deliveryMethod: charge.details.delivery_method,
              scopeArea: charge.details.area_in_sqm,
              price: charge.price.subtotal - discounts,
            }
          }),
          siteDeploymentFee: siteDeploymentFee?.price.base,
          totalNoTax,
          tax,
          total,
        }
      }
      return result;
    } catch (error) {
      captureError(error);
      return null;
    }
  }, [currentProject, job, metadata.quote_data, quoteCharges, rfqCharges]);

  useClickOutside(
    refs.reference,
    () => setIsDropdownOpen(false),
    ['.ActivityDropdownMenu'],
    false
  );

  // Close dropdown when scrolling
  useEventListener(
    'scroll',
    () => {
      if (isDropdownOpen) {
        setIsDropdownOpen(false);
      }
    },
    window,
    { capture: true }
  );

  const onDeleteActivity = () => {
    dispatch(
      showModal(CONFIRM_MODAL, {
        onConfirm: () => dispatch(deleteActivity(project_id, id)),
        title:
          type === ACTIVITY_TYPES.note
            ? text('deleteNote')
            : text('deleteFileUploadActivity'),
        body:
          type === ACTIVITY_TYPES.note
            ? text('deleteNoteBody')
            : text('deleteFileUploadActivityBody'),
        okLabel: text('delete'),
      })
    );
  };

  const onDeleteAsset = (assetId: string) => {
    dispatch(
      showModal(CONFIRM_MODAL, {
        onConfirm: () => dispatch(deleteAsset(project_id, job_id, id, assetId)), // id = activityId
        title: text('deleteFile'),
        body: text('deleteFileBody'),
        okLabel: text('delete'),
      })
    );
  };

  const getActivityIcon = () => {
    // handle old File Downloaded activities w/c are still under assets
    if (isActivityFileDownload({ title, description }) && type !== 'larki') {
      return TypeIconMap.alert;
    }
    // @ts-expect-error
    return TypeIconMap[type] || 'speech';
  };

  const getActivityType = (type: string) => {
    // handle old File Downloaded activites that were saved as notes
    if (
      [ACTIVITY_TYPES.note, ACTIVITY_TYPES.alert].includes(type) &&
      isActivityFileDownload({ title, description })
    ) {
      return 'download';
    }
    return type;
  };

  const onSupersede = () => {
    dispatch(
      showModal(CONFIRM_MODAL, {
        onConfirm: async () => {
          try {
            const result = await supersedeActivity({
              projectId: project_id,
              activityId: id,
              isSuperseded,
            }).unwrap();

            // TODO: Remove this once most of API migrated to
            // RTK query then we can use invalidateTags
            dispatch({
              type: SUPERSEDE_ACTIVITY,
              payload: {
                projectId: project_id,
                activityId: id,
                supersededAssets: result,
              },
            });
          } catch (error) {
            dispatch({
              type: alertConstants.ALERT_ERROR,
              payload: { message: error instanceof AxiosError ? error.response?.data?.message : 'Unknown error' },
            });
          }
        },
        title: text('supersedeConfirmTitle', {
          action: isSuperseded ? 'reverse supersede' : 'supersede',
        }),
        body: text('supersedeConfirmBody', {
          action: isSuperseded ? 'Reversed superseded' : 'Superseded',
          include: isSuperseded ? 'be' : 'not be',
        }),
        okLabel: text('supersedeConfirmYes', {
          action: isSuperseded ? 'reverse supersede' : 'supersede',
        }),
        cancelLabel: text('supersedeConfirmNo'),
        isLinkButton: true,
        isButtonFullWidth: false,
        isFooterButtonReversed: true,
        isAttentionIconVisible: true,
        isCloseButtonVisible: true,
      })
    );
  };

  useEffect(() => {
    let isMounted = true;
    const fetchGeocodedAddress = async () => {
      if (
        job_id &&
        !isEmpty(currentProject?.jobs) &&
        addressStatus === 'checking'
      ) {
        const fullAddress = currentProject.jobs.find(
          (job: any) => job.id === job_id
        )?.full_address;
        if (fullAddress) {
          try {
            const place = await geocode(fullAddress);
            if (isMounted) {
              const shortenedAddress = getShortenedAddressFromGeocoder(place);
              setShortenedAddress(shortenedAddress);
            }
          } catch (error) {
            console.error('Geocode error:', error);
          }
        }
      }

      if (isMounted) {
        setAddressStatus('done');
      }
    };
    fetchGeocodedAddress();
    return () => {
      isMounted = false;
    };
  }, [job_id, currentProject?.jobs, addressStatus, geocode]);

  const activityMetadata = useMemo(() => {
    if (job && type === ACTIVITY_TYPES.order && metadata) {
      return metadata;
    }
    return null;
  }, [job, type, metadata]);

  const { jobActivity } = props;

  const isSuperseded = superseded_assets.length > 0;

  const isDeleteActivityOptionVisible =
    isAdministrator(currentUser?.role) && !isNil(project_id);

  const isSupersedeButtonOptionVisible =
    created_assets && created_assets.length > 0;

  return !isActivityType(jobActivity, 'status') ? (
    <Activity
      className={classnames('JobActivity', {
        isStaff: isStaff(visibility),
      })}
    >
      <>
        <Activity.Icon iconProps={{ icon: getActivityIcon() }} />
        <Activity.Card className={classnames({ superseded: isSuperseded })}>
          <Activity.Card.Header>
            <Row className='m-0 w-100'>
              <div className='main-content font-weight--500 font-12'>
                <Activity.Card.Type
                  type={type ? capitalize(getActivityType(type)) : null}
                />
                <Activity.Card.Date
                  hasPipeSeparator={job_id || project_id}
                  date={formatDate(created_at)}
                  time={formatTime(created_at, 'h:mmaaa')}
                />

                {project_id ? (
                  <Activity.Card.HeaderElement
                    className={classnames(
                      {
                        hasPipeSeparator: !!job_id,
                      },
                      'projectId'
                    )}
                  >
                    {text('projectId')}:{project_id}
                  </Activity.Card.HeaderElement>
                ) : null}

                {job_id ? (
                  <Activity.Card.HeaderElement className='jobId'>
                    {text('orderId')}:{job_id}
                  </Activity.Card.HeaderElement>
                ) : null}

                {is_notification_sent ? (
                  <OverlayTrigger
                    delay={{ show: 250, hide: 250 }}
                    placement='auto'
                    overlay={
                      <Tooltip id='notif-sent' className='notification-sent'>
                        {text('notificationSent')}
                      </Tooltip>
                    }
                  >
                    <Icon icon='envelope' />
                  </OverlayTrigger>
                ) : null}
              </div>
              {isStaff(visibility) && (
                <span className='larki-tag'>{`[${text('larkiOnly')}]`} </span>
              )}
              <Activity.Card.HeaderRight classNames='d-flex flex-row align-items-center justify-content-end flex-fill'>
                <span className='mr-1'>
                  {isSuperseded ? text('supersededStatus') : null}
                </span>
                {(!created_user?.full_name || isStaff(created_user?.role)) &&
                  !isStaff(currentUser?.role)
                  ? 'LARKI'
                  : created_user?.full_name}

                <QuotePdfPreview quote={quotePdfProps} rootRef={scrollContainerRef} noTrigger>
                  {isDeleteActivityOptionVisible ||
                    isSupersedeButtonOptionVisible || quotePdfProps ? (
                    <div className='ActivityDropdownMenuContainer position-relative'>
                      <IconButton
                        // @ts-expect-error
                        icon='more'
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        ref={refs.setReference}
                        className='ActivityDropdownMenuTrigger ml-2'
                      />
                      {isDropdownOpen ? (
                        <FloatingPortal>
                          <div
                            ref={refs.setFloating}
                            style={{
                              ...floatingStyles,
                            }}
                            className='ActivityDropdownMenu dropdown-menu show'
                          >
                            <div
                              ref={arrowRef}
                              style={{
                                ...arrowStyles,
                                boxShadow: isFlipped
                                  ? '2px 2px 2.546px 0px rgba(0, 0, 0, 0.04)'
                                  : '-2px -2px 2.546px 0px rgba(0, 0, 0, 0.04)',
                              }}
                              className='dropdown-arrow'
                            />

                            {/* Show "Preview PDF" if this activity is an Estimate/RFQ or a Quote */}
                            {quotePdfProps ? (
                              <DialogTrigger asChild>
                                <Dropdown.Item className='py-2' onClick={() => setIsDropdownOpen(false)}>
                                  <div className='d-flex flex-row align-items-center'>
                                    <Icon
                                      icon='file-download'
                                      className='mr-2 size-20 default-stroke'
                                      // @ts-expect-error
                                      style={{ marginLeft: '-2px' }} // ad hoc inline style to align icon with text
                                    />
                                    <p className='font-14 m-0 mt-1'>
                                      Preview PDF
                                    </p>
                                  </div>
                                </Dropdown.Item>
                              </DialogTrigger>
                            ) : null}

                            {created_assets && created_assets.length > 0 ? (
                              <Dropdown.Item
                                onClick={() => {
                                  onSupersede();
                                  setIsDropdownOpen(false);
                                }}
                                className='py-2'
                              >
                                <div className='d-flex flex-row align-items-center'>
                                  <Icon
                                    icon={
                                      isSuperseded
                                        ? 'u_folder-add'
                                        : 'u_folder-minus'
                                    }
                                    className='original default-stroke mr-2'
                                  />
                                  <p className='font-14 m-0'>
                                    {isSuperseded
                                      ? 'Reverse supersede to include '
                                      : 'Supersede to exclude '}
                                    from &quot;Download all&quot;
                                  </p>
                                </div>
                              </Dropdown.Item>
                            ) : null}
                            {isAdministrator(currentUser?.role) &&
                              !isNil(project_id) ? (
                              <Dropdown.Item
                                onClick={() => {
                                  onDeleteActivity();
                                  setIsDropdownOpen(false);
                                }}
                                className='py-2'
                              >
                                <div className='d-flex flex-row align-items-center'>
                                  <Icon
                                    icon='trash'
                                    className='mr-2 size-20 default-stroke danger'
                                    // @ts-expect-error
                                    style={{ marginLeft: '-2px' }} // ad hoc inline style to align icon with text
                                  />
                                  <p className='font-14 m-0 mt-1 font-danger'>
                                    {text('deleteActivity')}
                                  </p>
                                </div>
                              </Dropdown.Item>
                            ) : null}
                          </div>
                        </FloatingPortal>
                      ) : null}
                    </div>
                  ) : null}
                </QuotePdfPreview>

              </Activity.Card.HeaderRight>
            </Row>
            {addressStatus === 'checking' ? (
              <div className='position-relative w-100'>
                <PulseLoader size={8} />
              </div>
            ) : shortenedAddress ? (
              <Row className='w-100 d-flex font-weight--400 font-12'>
                {shortenedAddress}
              </Row>
            ) : null}
          </Activity.Card.Header>
          <div className='ActivityContent'>
            {!isEmpty(metadata) ? (
              <ActivityTemplate
                metadata={activityMetadata}
                job={job}
                rfqCharges={rfqCharges}
                // @ts-expect-error
                quoteCharges={quoteCharges}
                description={description}
              />
            ) : (
              <>
                <div className='ActivityTitle font-16 font-weight--600 text-uppercase'>
                  {title}
                </div>
                {created_assets ? (
                  <ActivityAsset
                    // @ts-expect-error
                    onDelete={onDeleteAsset}
                    projectId={project_id}
                    assets={created_assets}
                    currentUser={currentUser}
                    isSuperseded={isSuperseded}
                  />
                ) : null}
                <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
                  {description}
                </ReactMarkdown>
              </>
            )}
          </div>
        </Activity.Card>
      </>
    </Activity>
  ) : (
    <JobStatusUpdate
      detailedStatuses={detailed_status_update}
      createdDate={created_at}
      activityTitle={title}
      activityDescription={description}
      jobId={job_id}
      projectId={project_id}
    />
  );
};

export default JobActivity;
